import React, { useEffect, useState } from "react";
import { Plus, Trash, Trophy, Medal, Lightning, Share } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import ShareCardModal from "@/components/ShareCardModal";

const UNITS = ["lbs", "kg", "reps", "sec", "min", "miles", "km"];

export default function PRs() {
  const [prs, setPrs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ exercise: "", value: "", unit: "lbs", note: "" });
  const [shareTarget, setShareTarget] = useState(null);

  const load = () => api.get("/prs").then(({ data }) => setPrs(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.exercise.trim() || !form.value) return toast.error("Exercise & value required");
    try {
      await api.post("/prs", { ...form, value: Number(form.value) });
      toast.success("PR locked in. Beast.");
      setOpen(false);
      setForm({ exercise: "", value: "", unit: "lbs", note: "" });
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/prs/${id}`);
    toast.success("Deleted");
    load();
  };

  // Group by exercise, show best per exercise
  const grouped = {};
  prs.forEach((p) => {
    const key = `${p.exercise}|${p.unit}`;
    if (!grouped[key] || p.value > grouped[key].best.value) {
      grouped[key] = { exercise: p.exercise, unit: p.unit, best: p, history: [] };
    }
  });
  prs.forEach((p) => {
    const key = `${p.exercise}|${p.unit}`;
    if (grouped[key]) grouped[key].history.push(p);
  });
  const records = Object.values(grouped).sort((a, b) => b.best.value - a.best.value);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="prs-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-tiny text-brand-accent mb-2">// HALL OF RECORDS</div>
          <h1 className="font-heading text-4xl sm:text-5xl">PERSONAL RECORDS</h1>
          <p className="text-text-secondary text-sm mt-2">Auto-detected from workouts + manual entries.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-kinetic px-5 py-3 text-xs label-tiny tracking-widest flex items-center gap-2" data-testid="add-pr-button">
              <Plus size={14} weight="bold" /> LOG PR
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-md" data-testid="pr-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">LOG PR</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="label-tiny block mb-2">EXERCISE</label>
                <Input value={form.exercise} onChange={(e) => setForm({ ...form, exercise: e.target.value })} placeholder="Bench Press" className="bg-surface-elevated border-white/10" data-testid="pr-exercise-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-tiny block mb-2">VALUE</label>
                  <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="bg-surface-elevated border-white/10" data-testid="pr-value-input" />
                </div>
                <div>
                  <label className="label-tiny block mb-2">UNIT</label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="pr-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                      {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="label-tiny block mb-2">NOTE</label>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional context" className="bg-surface-elevated border-white/10" data-testid="pr-note-input" />
              </div>
              <button onClick={save} className="btn-kinetic w-full py-3 text-xs label-tiny tracking-widest" data-testid="save-pr-button">
                LOCK IN PR
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {records.length === 0 ? (
        <div className="tactical-card p-12 text-center" data-testid="prs-empty">
          <Trophy size={36} weight="duotone" className="text-brand-accent mx-auto mb-4" />
          <h3 className="font-heading text-xl mb-2">NO RECORDS YET</h3>
          <p className="text-sm text-text-secondary">Log a workout with weight to auto-track PRs, or add one manually.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="prs-list">
          {records.map((r) => (
            <div key={`${r.exercise}-${r.unit}`} className="tactical-card p-6 active" data-testid={`pr-card-${r.exercise}`}>
              <div className="flex items-start justify-between mb-2">
                <Trophy size={20} weight="fill" className="text-brand-accent" />
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShareTarget(r.best)}
                    className="text-text-secondary hover:text-brand-accent transition-colors"
                    data-testid={`share-pr-${r.best.pr_id}`}
                    title="Share PR card"
                  >
                    <Share size={16} weight="bold" />
                  </button>
                  <span className="label-tiny">{r.unit.toUpperCase()}</span>
                </div>
              </div>
              <h3 className="font-heading text-lg mb-1">{r.exercise.toUpperCase()}</h3>
              <div className="flex items-baseline gap-2 my-2">
                <span className="font-heading text-4xl tabular-nums neon-yellow text-brand-accent">{r.best.value}</span>
                <span className="text-text-secondary">{r.unit}</span>
              </div>
              <div className="text-xs text-text-secondary mb-3">
                {new Date(r.best.date).toLocaleDateString()}
                {r.best.auto && <span className="ml-2 text-brand-primary">// AUTO</span>}
              </div>
              {r.history.length > 1 && (
                <div className="pt-3 border-t border-white/5">
                  <div className="label-tiny mb-2">HISTORY ({r.history.length})</div>
                  <div className="space-y-1 max-h-24 overflow-y-auto no-scrollbar">
                    {r.history.slice(0, 5).map((h) => (
                      <div key={h.pr_id} className="flex justify-between items-center text-xs">
                        <span className="text-text-secondary tabular-nums">{h.value} {h.unit}</span>
                        <button onClick={() => remove(h.pr_id)} className="text-text-secondary hover:text-red-400" data-testid={`delete-pr-${h.pr_id}`}>
                          <Trash size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ShareCardModal open={!!shareTarget} onClose={() => setShareTarget(null)} pr={shareTarget} />
    </div>
  );
}
